/**
 * Section icon glyphs for the rail.
 *
 * A Drop section stores an icon as a NAME (`"trophy"`, `"video"`,
 * `"newspaper"`, `"gamepad"`). Nothing in web consumed that field — the rail
 * rendered labels with a blank tile beside them, which is the difference
 * between the anchor's nav and a list of words.
 *
 * ## Why hand-written SVG and not an icon package
 *
 * `react-icons` is already a dependency and would be the obvious answer. It is
 * the wrong one here: its sets are filled glyphs at varying optical weights,
 * and the anchor's are a single family of 17px, 2px-stroke, round-capped line
 * icons. Mixing a filled trophy into that rail is visible immediately — the
 * tile beside it reads heavier than its neighbours and the column stops
 * looking like one set. These are drawn to the anchor's own stroke spec, which
 * is why they are here rather than imported.
 *
 * ## Unknown names render nothing, deliberately
 *
 * A section whose icon we do not have gets a label with no tile rather than a
 * fallback glyph. A wrong icon is worse than none: it tells the viewer this
 * section is something it is not, and it does it silently. The absence is also
 * the signal to add the missing one here.
 */

import React from "react";

const S = {
    width: 17,
    height: 17,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
};

const GLYPHS: Record<string, React.ReactNode> = {
    /* The anchor's own five, traced from `publisher-drop-hub.html` lines 968-996. */
    flag: <path d="M4 21V4M4 4h11l-1.5 4L15 12H4" />,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /></>,
    route: <><path d="M4 20 20 4" /><path d="M14 4h6v6" /><path d="M4 8V4h4" /><path d="m4 4 6 6" /></>,
    broadcast: <><circle cx="12" cy="12" r="2" /><path d="M7.5 7.5a6 6 0 0 0 0 9M16.5 16.5a6 6 0 0 0 0-9" /><path d="M4.5 4.5a10 10 0 0 0 0 15M19.5 19.5a10 10 0 0 0 0-15" /></>,
    users: <><path d="M17 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 20v-2a4 4 0 0 0-3-3.87" /></>,
    archive: <><path d="M4 4h16v5H4zM6 9v11h12V9" /><path d="M10 13h4" /></>,
    box: <><path d="M3 8h18v13H3z" /><path d="M3 8 5 3h14l2 5M12 8v13" /></>,

    /* The names Drop sections actually store today. */
    trophy: <><path d="M8 4h8v5a4 4 0 0 1-8 0z" /><path d="M8 5H5v2a3 3 0 0 0 3 3M16 5h3v2a3 3 0 0 1-3 3" /><path d="M12 13v4M9 20h6" /></>,
    video: <><path d="m16 10 5-3v10l-5-3z" /><rect x="3" y="6" width="13" height="12" rx="2" /></>,
    newspaper: <><path d="M4 5h13v14H4z" /><path d="M17 9h3v8a2 2 0 0 1-3 2" /><path d="M7 9h7M7 13h7M7 16h4" /></>,
    gamepad: <><path d="M7 12h4M9 10v4" /><circle cx="16" cy="11" r=".6" fill="currentColor" /><circle cx="18" cy="13" r=".6" fill="currentColor" /><rect x="2" y="6" width="20" height="12" rx="5" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 11h18" /></>,
    star: <path d="m12 3 2.6 5.6 6 .8-4.4 4.2 1.1 6.1L12 16.8 6.7 19.7l1.1-6.1L3.4 9.4l6-.8z" />,
    shopping: <><path d="M6 6h15l-1.6 8.4a2 2 0 0 1-2 1.6H9.2a2 2 0 0 1-2-1.7L5.6 4H3" /><circle cx="10" cy="20" r="1.2" /><circle cx="17" cy="20" r="1.2" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
    circle: <circle cx="12" cy="12" r="8" />,

    /* Glyphs the TYPE fallback below needs and no stored name reaches. */
    board: <><path d="M4 20V10M10 20V4M16 20v-7M4 20h16" /></>,
    wheel: <><circle cx="12" cy="12" r="9" /><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" /></>,
    bolt: <path d="M13 3 5 14h6l-1 7 8-11h-6z" />,
    check: <><rect x="4" y="5" width="16" height="14" rx="2" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>,
    /* A metal detector: grip, shaft, and the search coil at the ground. */
    detector: <><path d="M7 3h4M9 3v3M9.5 6.5 15 15" /><ellipse cx="16.5" cy="17.5" rx="5" ry="2.6" /></>,
    /* Earning: a coin, stacked. The bolt already belongs to the streak. */
    coins: <><ellipse cx="12" cy="7" rx="7" ry="3" /><path d="M5 7v5c0 1.7 3.1 3 7 3s7-1.3 7-3V7" /><path d="M5 12v5c0 1.7 3.1 3 7 3s7-1.3 7-3v-5" /></>,
    sparkle: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
};

/**
 * The section TYPE's glyph — what a rail item falls back to.
 *
 * `DropSection.icon` DEFAULTS to `"circle"`, so a section nobody hand-picked an
 * icon for arrives with a name that is not a picture of anything. Rendering
 * nothing for it left the anchor's tile empty on every row; rendering a circle
 * on all seven is the same list of words with a bullet. The type is real data
 * and is exactly what the tile is meant to say, so it answers instead — and a
 * hand-picked name still wins, because that is what picking one is for.
 */
const TYPE_GLYPHS: Record<string, string> = {
    /* `SurfaceSectionType`, every member. */
    GAMES: "gamepad",
    MISSIONS: "flag",
    TOURNAMENTS: "trophy",
    LEAGUES: "trophy",
    LADDERS: "route",
    LEADERBOARDS: "board",
    GUILDS: "users",
    CHALLENGES: "target",
    CAMPAIGNS: "calendar",
    EXCLUSIVES: "star",
    STREAMING: "broadcast",
    INFO: "info",
    CUSTOM: "star",
    NEWS: "newspaper",
    ENGAGEMENT: "sparkle",
    OFFICIAL_FOR: "archive",
};

/**
 * `DropEngagementType`, for ENGAGEMENT sections — which are ALL called
 * ENGAGEMENT, so the type alone would give a whole rail one glyph. What the
 * section actually holds is the answer.
 */
const ENGAGEMENT_GLYPHS: Record<string, string> = {
    GAMIFICATION: "wheel",
    COMPETITION: "board",
    CHALLENGE: "target",
    QUEST: "flag",
    ACTIVATION: "bolt",
};

/**
 * The glyph for a section, or nothing.
 *
 * Order: the hand-picked name, then what the section holds, then its type. The
 * name is skipped when it is the model's `"circle"` default, which is a stored
 * value nobody chose — treating it as a choice is what left every tile empty.
 */
export function sectionIcon(name?: string, type?: string, engagementType?: string): React.ReactNode {
    const picked = name && name.toLowerCase() !== "circle" ? GLYPHS[name.toLowerCase()] : undefined;
    const byEngagement = engagementType ? GLYPHS[ENGAGEMENT_GLYPHS[engagementType.toUpperCase()] ?? ""] : undefined;
    const byType = type ? GLYPHS[TYPE_GLYPHS[type.toUpperCase()] ?? ""] : undefined;
    const glyph = picked ?? byEngagement ?? byType ?? (name ? GLYPHS[name.toLowerCase()] : undefined);
    return glyph ? <svg {...S} aria-hidden="true">{glyph}</svg> : null;
}

/**
 * `SurfaceContentBlockType`, for the nav entries a placed BLOCK earns.
 *
 * Every block entry used to be drawn with a star, so Earn, Gear and Rules
 * were three identical tiles — a tile that says nothing about what it opens
 * is the same as no tile, and worse, because it claims to.
 */
const BLOCK_GLYPHS: Record<string, string> = {
    ACHIEVEMENTS: "trophy",
    PRODUCTS: "detector",
    FAQ: "info",
    LEADERBOARD: "board",
    STATS: "board",
    EARNING: "coins",
    PRIZES: "trophy",
    STEPS: "route",
    PROMO: "star",
    ROSTER: "users",
    MEDIA: "video",
    CONTENT: "newspaper",
};

/**
 * The glyph for a placed content block's nav entry.
 *
 * The block's own `navIcon` wins; the type table below is only the default.
 * Without the authored field a second EARNING block could not carry its own
 * mark, and a block type absent from the table rendered no tile at all with no
 * way to fix it from admin.
 */
export function blockIcon(blockType?: string, navIcon?: string): React.ReactNode {
    const name = navIcon?.trim() || (blockType ? BLOCK_GLYPHS[blockType.toUpperCase()] : undefined);
    const glyph = name ? GLYPHS[name] : undefined;
    return glyph ? <svg {...S} aria-hidden="true">{glyph}</svg> : null;
}
